
import { v4 as uuidv4 } from "uuid";
import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  BastionFacility,
  BastionItem,
  CharacterLimitation,
  CreatureLimitation,
  ItemLimitation,
  Limitation,
} from "@dndModels";

import DeleteButton from "@components/input/buttons/DeleteButton";
import StringBox from "@components/input/strings/StringBox";
import DescriptionBox from "@dndComponents/DescriptionBox";
// import ItemLimitInput from '../limitations/ItemLimitInput';
import CharacterLimitationBox from "@dndComponents/limitations/CharacterLimitationBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";
// import CreatureLimitInput from '../limitations/CreatureLimitInput';
import AddButton from "@components/input/buttons/AddButton";
import LimitationInput from "@dndComponents/limitations/LimitationInput";

interface Props {
  bf: BastionFacility;
  item: BastionItem;
  onChange: () => void; 
  onDelete: () => void; 
  disabled?: boolean;
}

export default function BastionItemInput({ 
  bf,
  item,
  onChange,
  onDelete,
  disabled = false,
}: Props) {
  const [reloading, setReloading] = useState(false);
  const [ping, setPing] = useState(false);
  
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
     
  }, [reloading]);

  const onCustomAdded = () => {
    item.custom_options.push({ true_id: uuidv4().toString(), name: "", count: 1, value: 0 });
    onChange();
    setPing(!ping);
  }

  const renderCustom = () => {
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          size={{
            xs: 12
          }}>
          <span className="header padded">
            Add Custom Option
          </span>
          { !disabled && 
            <AddButton onClick={onCustomAdded} />
          }
        </Grid>
        { renderCustomOptions() }
      </Grid>
    );
  }

  const renderCustomOptions = () => {
    return (
      <>
        { !reloading && item.custom_options.map((opt, key) => {
          return (
            <Grid
              key={key}
              size={{
                xs: 12
              }}>
              <Grid container spacing={0} direction="row">
                <Grid
                  size={{
                    xs: 3
                  }}>
                  <StringBox 
                    name="Custom Option"
                    value={opt.name}
                    disabled={disabled}
                    onBlur={(changed: string) => {
                      item.custom_options[key].name = changed;
                      onChange();
                      setPing(!ping);
                    }}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 3
                  }}>
                  <StringBox 
                    name="Count"
                    value={opt.count}
                    disabled={disabled}
                    type="number"
                    onBlur={(changed: string) => {
                      item.custom_options[key].count = +changed;
                      onChange();
                      setPing(!ping);
                    }}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 3
                  }}>
                  <StringBox 
                    name="Value"
                    value={opt.value}
                    disabled={disabled}
                    type="number"
                    onBlur={(changed: string) => {
                      item.custom_options[key].value = +changed;
                      onChange();
                      setPing(!ping);
                    }}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 3
                  }}>
                  <DeleteButton
                    disabled={disabled}
                    onClick={() => { 
                      item.custom_options.splice(key, 1);
                      onChange();
                      setReloading(true);
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          );
        })}
      </>
    );
  }

  const renderForSubtypes = () => {
    if (bf.subtypes.length > 0) {
      return (
        <>
          <Grid
            size={{
              xs: 12
            }}>
            <SelectStringBox
              name="For Subtypes"
              options={bf.subtypes}
              values={item.for_subtypes}
              disabled={disabled}
              multiple
              onChange={(changed: string[]) => {
                item.for_subtypes = changed;
                onChange();
                setPing(!ping);
              }}
            />
          </Grid>
        </>
      );
    }
    return null;
  }

  return (
    <Grid container spacing={0} direction="row">
      { !disabled &&
        <>
          <Grid
            size={{
              xs: 12
            }}>
            <DeleteButton onClick={onDelete} />
          </Grid>
        </>
      }
      { 
        <>
          <Grid
            size={{
              xs: 12
            }}>
            <StringBox
              name="Name"
              value={item.name}
              disabled={disabled}
              onBlur={(changed: string) => {
                item.name = changed;
                onChange();
                setPing(!ping);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12
            }}>
            <DescriptionBox
              name="Description"
              value={item.description}
              disabled={disabled}
              onChange={(changed: string) => {
                item.description = changed;
                onChange();
                setPing(!ping);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12
            }}>
            <SelectStringBox
              name="Type"
              value={item.type}
              options={["Item","Creature","Custom"]}
              disabled={disabled}
              onChange={(changed: "Item" | "Creature" | "Custom") => {
                item.type = changed;
                onChange();
                setPing(!ping);
              }}
            />
          </Grid>
          <Grid
            className="header"
            size={{
              xs: 12
            }}>Item Limits</Grid>
          <Grid
            size={{
              xs: 12
            }}>
            { (item.type === "Item" || item.type === "Creature") ?
              <LimitationInput 
                obj={item.type === "Item" ? item.item_limit : item.creature_limit} 
                disabled={disabled}
                change_time={0}
                onDelete={() => {
                  
                }}
                onChange={(changed: Limitation) => {
                  if (changed instanceof ItemLimitation) {
                    item.item_limit.copy(changed);
                  } else if (changed instanceof CreatureLimitation) {
                    item.creature_limit.copy(changed);
                  }
                  onChange();
                  setPing(!ping);
                }}
              />
            : item.type === "Custom" &&
              renderCustom()
            }
            {/* { item.type === "Item" ? 
              <ItemLimitInput 
                obj={item.item_limit} 
                onChange={(changed: ItemLimitation) => {
                  item.item_limit.copy(changed);
                  onChange();
                  setPing(!ping);
                }}
              />
            : item.type === "Creature" ?
              <CreatureLimitInput 
                obj={item.creature_limit} 
                onChange={(changed: CreatureLimitation) => {
                  item.creature_limit.copy(changed);
                  onChange();
                  setPing(!ping);
                }}
              />
            : item.type === "Custom" &&
              renderCustom()
            } */}
          </Grid>
          { renderForSubtypes() }
          <Grid
            className="header"
            size={{
              xs: 12
            }}>Character Limits</Grid>
          <Grid
            size={{
              xs: 12
            }}>
            <CharacterLimitationBox 
              obj={item.char_limit} 
              onChange={(changed: CharacterLimitation) => {
                item.char_limit.copy(changed);
                onChange();
                setPing(!ping);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 4
            }}>
            <StringBox
              name="Count"
              value={`${item.count}`}
              disabled={disabled}
              type="number"
              onBlur={(changed: string) => {
                item.count = +changed;
                onChange();
                setPing(!ping);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 4
            }}>
            <StringBox
              name="Time (days)"
              value={`${item.time_days}`}
              disabled={disabled}
              type="number"
              onBlur={(changed: string) => {
                item.time_days = +changed;
                onChange();
                setPing(!ping);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 4
            }}>
            <StringBox
              name="Cost (Gold Pieces)"
              value={`${item.cost_gold}`}
              disabled={disabled}
              type="number"
              onBlur={(changed: string) => {
                item.cost_gold = +changed;
                onChange();
                setPing(!ping);
              }}
            />
          </Grid>
        </>
      }
    </Grid>
  );
}
